import { useEffect, useState } from 'react'
import projects from './components/Projects/data.json'
import { experiences } from './components/Experience/data'

const nav = ['work', 'experience', 'toolkit', 'contact']
const featured = projects.slice(0, 3)
const toolGroups = [
    ['Java', 'Spring Boot', 'Node.js', 'Express', 'REST APIs'],
    ['React', 'TypeScript', 'JavaScript', 'Next.js', 'Tailwind'],
    ['PostgreSQL', 'MongoDB', 'Kafka', 'Docker', 'Kubernetes', 'AWS'],
]

function Arrow() {
    return <span aria-hidden='true'>↗</span>
}

function App() {
    const [dark, setDark] = useState(() => {
        const saved = localStorage.getItem('portfolio-theme')
        return saved ? saved === 'dark' : true
    })
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        document.documentElement.dataset.theme = dark ? 'dark' : 'light'
        localStorage.setItem('portfolio-theme', dark ? 'dark' : 'light')
    }, [dark])

    return (
        <div className='site-shell'>
            <header className='nav-wrap'>
                <a
                    className='wordmark'
                    href='#top'
                    aria-label='Roshan Kumar, home'>
                    RK<span>.</span>
                </a>
                <nav
                    className={menuOpen ? 'nav-links open' : 'nav-links'}
                    aria-label='Main navigation'>
                    {nav.map((item) => (
                        <a
                            key={item}
                            href={`#${item}`}
                            onClick={() => setMenuOpen(false)}>
                            {item}
                        </a>
                    ))}
                </nav>
                <div className='nav-actions'>
                    <button
                        className='icon-button'
                        onClick={() => setDark(!dark)}
                        aria-label='Toggle color theme'>
                        {dark ? '☼' : '◐'}
                    </button>
                    <button
                        className='menu-button'
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-expanded={menuOpen}>
                        {menuOpen ? 'Close' : 'Menu'}
                    </button>
                </div>
            </header>

            <main id='top'>
                <section className='hero'>
                    <div className='hero-copy'>
                        <p className='eyebrow'>
                            <span /> Available for the right opportunity
                        </p>
                        <h1>
                            I build systems that feel <em>simple.</em>
                        </h1>
                        <p className='hero-intro'>
                            I’m Roshan Kumar, a full-stack engineer shaping
                            reliable backend systems and thoughtful product
                            experiences—with a growing focus on AI workflows.
                        </p>
                        <div className='hero-cta'>
                            <a className='button primary' href='#work'>
                                Explore my work <Arrow />
                            </a>
                            <a
                                className='button text-button'
                                href='mailto:roshankumar_2022@outlook.com'>
                                Let’s talk
                            </a>
                        </div>
                    </div>
                    <aside className='hero-card' aria-label='Current profile'>
                        <div className='portrait-wrap'>
                            <img
                                src='/assets/Designer1-CJR_89zx.webp'
                                alt='Illustrated portrait of Roshan Kumar'
                            />
                            <span className='orbit orbit-one' />
                            <span className='orbit orbit-two' />
                        </div>
                        <div className='hero-card-meta'>
                            <div>
                                <small>Currently</small>
                                <strong>Systems Engineer at TCS</strong>
                            </div>
                            <div>
                                <small>Based in</small>
                                <strong>Kolkata, India</strong>
                            </div>
                        </div>
                    </aside>
                    <div className='hero-index' aria-hidden='true'>
                        01
                    </div>
                </section>

                <section className='statement'>
                    <p className='section-kicker'>What I bring</p>
                    <h2>
                        Product thinking on the surface.
                        <br />
                        <span>Systems thinking underneath.</span>
                    </h2>
                    <div className='principles'>
                        <article>
                            <b>01</b>
                            <h3>Build for reality</h3>
                            <p>
                                Scalable architecture, useful interfaces, and
                                decisions grounded in real constraints.
                            </p>
                        </article>
                        <article>
                            <b>02</b>
                            <h3>Make it legible</h3>
                            <p>
                                From API contracts to UI states, clarity is the
                                feature that makes every other feature work.
                            </p>
                        </article>
                        <article>
                            <b>03</b>
                            <h3>Own the outcome</h3>
                            <p>
                                I work across the stack and follow the problem
                                through—from first sketch to production.
                            </p>
                        </article>
                    </div>
                </section>

                <section className='work section-pad' id='work'>
                    <div className='section-heading'>
                        <div>
                            <p className='section-kicker'>Selected work</p>
                            <h2>Things I’ve shipped.</h2>
                        </div>
                        <a
                            href='https://github.com/roshan798'
                            target='_blank'
                            rel='noreferrer'>
                            View GitHub <Arrow />
                        </a>
                    </div>
                    <div className='project-grid'>
                        {featured.map((project, index) => (
                            <article
                                className={`project-card project-${index + 1}`}
                                key={project.title}>
                                <a
                                    className='project-visual'
                                    href={project.demoLink}
                                    target='_blank'
                                    rel='noreferrer'
                                    aria-label={`Open ${project.title}`}>
                                    <img src={project.img} alt='' />
                                    <span className='project-number'>
                                        0{index + 1}
                                    </span>
                                    <span className='project-link'>
                                        <Arrow />
                                    </span>
                                </a>
                                <div className='project-copy'>
                                    <div>
                                        <p>{project.duration}</p>
                                        <h3>{project.title}</h3>
                                    </div>
                                    <p className='project-description'>
                                        {project.description}
                                    </p>
                                    <ul>
                                        {project.stack
                                            .slice(0, 5)
                                            .map((tech) => (
                                                <li key={tech}>{tech}</li>
                                            ))}
                                    </ul>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className='experience section-pad' id='experience'>
                    <div className='section-heading'>
                        <div>
                            <p className='section-kicker'>Experience</p>
                            <h2>Where I’ve made impact.</h2>
                        </div>
                    </div>
                    {experiences
                        .filter((item) => item.show)
                        .map((item) => (
                            <article className='experience-card' key={item.id}>
                                <div className='experience-meta'>
                                    <p>
                                        {item.startDate} — {item.endDate}
                                    </p>
                                    <span>{item.location}</span>
                                </div>
                                <div className='experience-body'>
                                    <a
                                        href={item.companyUrl}
                                        target='_blank'
                                        rel='noreferrer'>
                                        {item.company} <Arrow />
                                    </a>
                                    <h3>{item.role}</h3>
                                    <p>{item.description}</p>
                                    <ul>
                                        {item.bullets.map((bullet) => (
                                            <li
                                                key={bullet}
                                                dangerouslySetInnerHTML={{
                                                    __html: bullet,
                                                }}
                                            />
                                        ))}
                                    </ul>
                                </div>
                                <div className='metric'>
                                    <strong>100K+</strong>
                                    <span>daily policy transactions</span>
                                    <strong>60%</strong>
                                    <span>faster policy changes</span>
                                </div>
                            </article>
                        ))}
                </section>

                <section className='toolkit section-pad' id='toolkit'>
                    <div className='section-heading'>
                        <div>
                            <p className='section-kicker'>Toolkit</p>
                            <h2>Built across the stack.</h2>
                        </div>
                        <p className='heading-note'>
                            A practical toolkit for turning product ideas into
                            reliable software.
                        </p>
                    </div>
                    <div className='tool-grid'>
                        {toolGroups.map((group, index) => (
                            <article key={group[0]}>
                                <span>0{index + 1}</span>
                                <h3>
                                    {
                                        [
                                            'Backend & APIs',
                                            'Frontend & Product',
                                            'Data & Infrastructure',
                                        ][index]
                                    }
                                </h3>
                                <div>
                                    {group.map((tool) => (
                                        <p key={tool}>{tool}</p>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className='contact section-pad' id='contact'>
                    <p className='section-kicker'>Start a conversation</p>
                    <h2>
                        Have a problem worth
                        <br />
                        <em>building for?</em>
                    </h2>
                    <p>
                        I’m interested in full-stack engineering, backend
                        systems, and ambitious AI-powered products.
                    </p>
                    <a
                        className='button primary'
                        href='mailto:roshankumar_2022@outlook.com'>
                        roshankumar_2022@outlook.com <Arrow />
                    </a>
                    <div className='contact-links'>
                        <a
                            href='https://github.com/roshan798'
                            target='_blank'
                            rel='noreferrer'>
                            GitHub <Arrow />
                        </a>
                        <a
                            href='https://www.linkedin.com/in/roshan-kumar7989/'
                            target='_blank'
                            rel='noreferrer'>
                            LinkedIn <Arrow />
                        </a>
                        <a
                            href='https://x.com/Roshank10254505'
                            target='_blank'
                            rel='noreferrer'>
                            X / Twitter <Arrow />
                        </a>
                    </div>
                </section>
            </main>

            <footer>
                <p>© {new Date().getFullYear()} Roshan Kumar</p>
                <p>Designed with intention. Built with care.</p>
                <a href='#top'>Back to top ↑</a>
            </footer>
        </div>
    )
}

export default App
